import {
  cloneValue,
  collectionKey,
  frozenClone,
  opaqueId,
} from "./preset-internals.js";

function hidden(key) {
  return frozenClone({ kind: "hidden", collectionKey: key });
}

function unavailable(key, reason) {
  return frozenClone({
    kind: "unavailable",
    collectionKey: key,
    boundary: "local-preset-sources",
    reason,
  });
}

function sourceProjection(state) {
  if (state.kind === "ready") {
    return { kind: "ready", revision: state.revision };
  }
  return {
    kind: state.kind,
    ...(state.reason === undefined ? {} : { reason: state.reason }),
  };
}

export function createLocalPresetSourceController({ repository } = {}) {
  const supported = typeof repository?.loadLocalSources === "function";
  const updatable = typeof repository?.updateLocalSource === "function";
  const states = new Map();
  const generations = new Map();

  function state(rawKey) {
    const key = collectionKey(rawKey);
    return states.get(key) ?? hidden(key);
  }

  function invalidate(rawKey) {
    const key = collectionKey(rawKey);
    generations.set(key, (generations.get(key) ?? 0) + 1);
    states.set(key, supported
      ? frozenClone({ kind: "loading", collectionKey: key })
      : hidden(key));
  }

  async function load(rawKey) {
    const key = collectionKey(rawKey);
    if (!supported) {
      const next = hidden(key);
      states.set(key, next);
      return next;
    }
    const generation = generations.get(key) ?? 0;
    let next;
    try {
      next = await repository.loadLocalSources(key);
    } catch (_error) {
      next = unavailable(key, "local-preset-source-load-failed");
    }
    if (generation !== (generations.get(key) ?? 0)) {
      return state(key);
    }
    if (
      !next
      || next.collectionKey !== key
      || !["hidden", "missing", "ready", "unavailable"].includes(next.kind)
      || (next.kind === "ready" && !Array.isArray(next.presets))
    ) {
      next = unavailable(key, "local-preset-source-contract-invalid");
    }
    const frozen = frozenClone(next);
    states.set(key, frozen);
    return frozen;
  }

  function projectionFields(rawKey) {
    const current = state(rawKey);
    return frozenClone({
      localPresetSources: sourceProjection(current),
      localPresets: current.kind === "ready"
        ? current.presets.map((preset) => ({
            ...cloneValue(preset),
            kind: "local",
          }))
        : (current.kind === "hidden" || current.kind === "missing" ? [] : null),
    });
  }

  async function resolve(rawKey, rawPresetId, { expectedPresetRevision } = {}) {
    const key = collectionKey(rawKey);
    const current = await load(key);
    if (current.kind !== "ready") {
      return frozenClone({
        ...current,
        boundary: "local-preset-sources",
        operation: "resolve-local-preset",
      });
    }
    const presetId = opaqueId(rawPresetId, "Local preset ID");
    const preset = current.presets.find((candidate) => candidate.id === presetId) ?? null;
    if (!preset) {
      throw new TypeError(`Unknown local preset ${presetId}.`);
    }
    if (
      expectedPresetRevision !== undefined
      && preset.revision !== opaqueId(
        expectedPresetRevision,
        "Expected local preset revision",
      )
    ) {
      return frozenClone({
        kind: "conflict",
        collectionKey: key,
        boundary: "local-preset-sources",
        operation: "resolve-local-preset",
        presetId,
        expectedPresetRevision,
        actualPresetRevision: preset.revision,
      });
    }
    return frozenClone({
      kind: "ready",
      collectionKey: key,
      preset,
    });
  }

  async function update(rawKey, rawPresetId, { payload, expectedPresetRevision } = {}) {
    const key = collectionKey(rawKey);
    if (!updatable) {
      return unavailable(key, "local-preset-source-update-unsupported");
    }
    const resolved = await resolve(key, rawPresetId, { expectedPresetRevision });
    if (resolved.kind !== "ready") {
      return frozenClone({
        ...resolved,
        boundary: "local-preset-sources",
        operation: "update-local-preset",
      });
    }
    const current = state(key);
    let next;
    try {
      next = await repository.updateLocalSource(key, resolved.preset.id, {
        payload: cloneValue(payload),
        expectedRevision: current.revision,
        expectedPresetRevision: resolved.preset.revision,
      });
    } catch (_error) {
      next = unavailable(key, "local-preset-source-update-failed");
    }
    if (
      next?.kind === "ready"
      && next.collectionKey === key
      && Array.isArray(next.presets)
    ) {
      const frozen = frozenClone(next);
      states.set(key, frozen);
      return frozen;
    }
    invalidate(key);
    await load(key);
    return frozenClone({
      ...next,
      collectionKey: key,
      boundary: "local-preset-sources",
      operation: "update-local-preset",
    });
  }

  function clear() {
    states.clear();
    generations.clear();
  }

  return Object.freeze({ clear, invalidate, load, projectionFields, resolve, state, update });
}
